import React from 'react';
import {
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import MiniPIGraph from './MiniPIGraph';

interface CandidateListItemProps {
  candidate: any;
  jobId: string;
  idealRanges?: {
    a: [number, number];
    b: [number, number];
    c: [number, number];
    d: [number, number];
  };
  job?: any; // To get relationship requirements
  onEdit?: (candidateId: string) => void;
  onDelete?: (candidateId: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const CandidateListItem: React.FC<CandidateListItemProps> = ({
  candidate,
  jobId,
  idealRanges,
  job,
  onEdit,
  onDelete,
  canEdit = false,
  canDelete = false,
}) => {
  const navigate = useNavigate();

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'screening': return 'info';
      case 'interviewing': return 'info';
      case 'evaluated': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'error';
      case 'withdrawn': return 'default';
      default: return 'default';
    }
  };

  // Calculate match percentage (simplified version for display)
  const getMatchPercentage = () => {
    if (!candidate.position_match_score) return null;
    // Convert 0-10 scale to percentage
    return Math.round(candidate.position_match_score * 10);
  };

  const matchPercentage = getMatchPercentage();

  // Get match color based on percentage
  const getMatchColor = (percentage: number | null) => {
    if (!percentage) return 'text.secondary';
    if (percentage >= 85) return 'success.main';
    if (percentage >= 70) return 'warning.main';
    if (percentage >= 55) return 'warning.dark';
    return 'error.main';
  };

  // Format application date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const hasPIData = candidate.pi_factor_a && candidate.pi_factor_b && 
                    candidate.pi_factor_c && candidate.pi_factor_d;

  // Parse match breakdown if available
  let matchBreakdown: any = null;
  if (candidate.match_breakdown) {
    try {
      matchBreakdown = typeof candidate.match_breakdown === 'string' 
        ? JSON.parse(candidate.match_breakdown) 
        : candidate.match_breakdown;
    } catch (e) {
      console.error('Failed to parse match breakdown:', e);
    }
  }

  // Get relationship requirements status
  const getRelationshipStatus = (): Array<{ label: string; met: boolean }> | null => {
    if (!matchBreakdown || !job) return null;
    
    const relationships: Array<{ label: string; met: boolean }> = [];
    const compScores = matchBreakdown.comparison_scores || {};
    
    // Check each relationship requirement - all 12 possible combinations
    const relationshipChecks = [
      // A greater than others
      { req: 'requires_a_greater_than_b', score: 'a_greater_than_b', label: 'A>B' },
      { req: 'requires_a_greater_than_c', score: 'a_greater_than_c', label: 'A>C' },
      { req: 'requires_a_greater_than_d', score: 'a_greater_than_d', label: 'A>D' },
      // B greater than others
      { req: 'requires_b_greater_than_a', score: 'b_greater_than_a', label: 'B>A' },
      { req: 'requires_b_greater_than_c', score: 'b_greater_than_c', label: 'B>C' },
      { req: 'requires_b_greater_than_d', score: 'b_greater_than_d', label: 'B>D' },
      // C greater than others
      { req: 'requires_c_greater_than_a', score: 'c_greater_than_a', label: 'C>A' },
      { req: 'requires_c_greater_than_b', score: 'c_greater_than_b', label: 'C>B' },
      { req: 'requires_c_greater_than_d', score: 'c_greater_than_d', label: 'C>D' },
      // D greater than others
      { req: 'requires_d_greater_than_a', score: 'd_greater_than_a', label: 'D>A' },
      { req: 'requires_d_greater_than_b', score: 'd_greater_than_b', label: 'D>B' },
      { req: 'requires_d_greater_than_c', score: 'd_greater_than_c', label: 'D>C' },
    ];

    relationshipChecks.forEach(check => {
      if (job[check.req]) {
        relationships.push({ 
          label: check.label, 
          met: compScores[check.score] === 1 
        });
      }
    });

    
    return relationships.length > 0 ? relationships : null;
  };

  const relationshipStatus = getRelationshipStatus();

  return (
    <ListItem
      data-testid={`candidate-list-item-${candidate.id}`}
      sx={{
        py: 2,
        px: 3,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      divider
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Link
                to={`/jobs/${jobId}/candidates/${candidate.id}`}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  data-testid={`candidate-name-${candidate.id}`}
                  sx={{
                    color: 'primary.main',
                    '&:hover': { textDecoration: 'underline' },
                    cursor: 'pointer',
                  }}
                >
                  {candidate.first_name} {candidate.last_name}
                </Typography>
              </Link>
              {candidate.pi_profile_code && (
                <Chip
                  label={candidate.pi_profile_code}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22 }}
                />
              )}
            </Box>
            <Box display="flex" gap={0.5}>
              <IconButton
                size="small"
                onClick={() => navigate(`/jobs/${jobId}/candidates/${candidate.id}`)}
                title="View candidate"
                data-testid={`view-candidate-${candidate.id}`}
              >
                <Visibility />
              </IconButton>
              {canEdit && (
                <IconButton
                  size="small"
                  onClick={() => onEdit ? onEdit(candidate.id) : navigate(`/candidates/${candidate.id}`)}
                  title="Edit candidate"
                  data-testid={`edit-candidate-${candidate.id}`}
                >
                  <Edit />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={() => onDelete && onDelete(candidate.id)}
                  color="error"
                  title="Archive candidate"
                  data-testid={`delete-candidate-${candidate.id}`}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          </Box>
        }
        secondary={
          <Stack spacing={1.5}>
            {/* Status and Date Row */}
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={candidate.status}
                size="small"
                color={getStatusColor(candidate.status) as any}
                sx={{ height: 24 }}
                data-testid={`candidate-status-${candidate.id}`}
              />
              <Typography variant="body2" color="text.secondary">
                {candidate.application_source === 'other' ? 'Imported' : 'Applied'}: {formatDate(candidate.application_date)}
              </Typography>
              {candidate.interview_date && (
                <Typography variant="body2" color="text.secondary">
                  Interview: {new Date(candidate.interview_date).toLocaleDateString()}
                </Typography>
              )}
            </Box>

            {/* PI Graph and Match Score */}
            {hasPIData && (
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box>
                  <MiniPIGraph
                    factorA={candidate.pi_factor_a}
                    factorB={candidate.pi_factor_b}
                    factorC={candidate.pi_factor_c}
                    factorD={candidate.pi_factor_d}
                    idealRanges={idealRanges}
                    size="small"
                    showLabels={true}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
                  {matchPercentage !== null && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={getMatchColor(matchPercentage)}
                          data-testid={`candidate-match-score-${candidate.id}`}
                        >
                          {matchPercentage}% Match
                        </Typography>
                        <Tooltip title={
                          <Box>
                            <Typography variant="body2">Match Score Breakdown:</Typography>
                            {matchBreakdown && (
                              <>
                                <Typography variant="caption">
                                  Factor Score: {matchBreakdown.factor_total?.toFixed(1) || 0}/4
                                </Typography>
                                <br />
                                <Typography variant="caption">
                                  Relationship Score: {matchBreakdown.comparison_total?.toFixed(1) || 0}/{(matchBreakdown.possible_total || 4) - 4}
                                </Typography>
                                <br />
                                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                                  Active Requirements: {Object.keys(matchBreakdown.comparison_scores || {}).filter(key => 
                                    matchBreakdown.comparison_scores[key] !== undefined && 
                                    matchBreakdown.breakdown?.position_requirements?.required_comparisons?.[key]
                                  ).length}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }>
                          <Typography variant="caption" color="text.secondary">
                            Hover for details
                          </Typography>
                        </Tooltip>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={matchPercentage}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: 'action.disabled',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getMatchColor(matchPercentage),
                          },
                        }}
                      />
                    </Box>
                  )}
                  
                  {/* Relationship requirements */}
                  {relationshipStatus && relationshipStatus.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {relationshipStatus.map((rel, idx) => (
                        <Chip
                          key={idx}
                          label={rel.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: rel.met ? 'success.light' : 'error.light',
                            color: rel.met ? 'success.dark' : 'error.dark',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Additional Info */}
            {(candidate.email || candidate.phone) && (
              <Box display="flex" gap={3}>
                {candidate.email && (
                  <Typography variant="caption" color="text.secondary">
                    Email: {candidate.email}
                  </Typography>
                )}
                {candidate.phone && (
                  <Typography variant="caption" color="text.secondary">
                    Phone: {candidate.phone}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        }
      />
    </ListItem>
  );
};

export default CandidateListItem;